import {Roles} from '@hconnect/apiclient'
import {useUserContext} from '@hconnect/login'
import {dataTestId} from '@hconnect/uikit'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {Box, Grid, Stack} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {Outlet} from 'react-router'

import {PlantStructureFilterOptions} from '../common/enums'
import {useGetRemovedPlantStructure} from '../common/hooks/queries/useRemovedPlantStructureQuery'

import {usePlantSetupFilter} from './hooks/plantSetup/usePlantSetupFilter'
import {PlantStructureNode} from './plant-setup-tree/PlantStructureNode'
import {PlantStructureTree} from './plant-setup-tree/PlantStructureTree'
import {RestorePlantStructureNode} from './plant-setup-tree/RestoreSetupTree/RestorePlantStructureNode'
import {ItemDetailsProvider, SelectedItemProvider, ItemLinkProvider} from './providers'

import {PageLayout, SettingsHelmet} from '@settings/modules/common/components'
import {QuickFilterOptionButton} from '@settings/modules/common/components/settingsTree'
import {RouteName} from '@settings/modules/routing'

export const PlantStructure = () => {
  const {t} = useTranslation()
  const {selectedFilterOption, handleSetFilter} = usePlantSetupFilter()

  const {loggedUser} = useUserContext()
  const userRoles = loggedUser?.hasRoles
  const canRestoreDepartments = !!userRoles && userRoles.includes(Roles.HproduceCCCSupporter)

  const roleBasedFilterOptions = () => {
    return Object.values(PlantStructureFilterOptions).map((option) => {
      if (option === PlantStructureFilterOptions.Removed && !canRestoreDepartments) {
        return null
      } else {
        return (
          <QuickFilterOptionButton
            data-selected={option === selectedFilterOption}
            onClick={() => handleSetFilter(option)}
            key={option}
            variant="outlined"
            {...dataTestId(`plant-structure-type-filter-${option}`)}
          >
            {t(`plantSetup.label.${option}`)}
          </QuickFilterOptionButton>
        )
      }
    })
  }

  return (
    <SelectedItemProvider>
      <ItemLinkProvider>
        <PageLayout
          title={t('upmSettings.plantSetup')}
          {...dataTestId(`page_${RouteName.PLANT_SETUP}`)}
        >
          <SettingsHelmet title={t('plantSetup.pageTitle')} />
          <ItemDetailsProvider>
            <Grid container spacing={2} {...dataTestId('plant-structure-container')}>
              <Grid item xs={12} sm={4}>
                <CardBox px={0}>
                  <Stack
                    direction="column"
                    justifyContent="space-between"
                    {...dataTestId('janus-plant-structure-menu')}
                    alignItems={'left'}
                    gap={2}
                  >
                    <Box
                      gap={1}
                      px={2}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        flexDirection: {xs: 'column', sm: 'row'}
                      }}
                    >
                      {roleBasedFilterOptions()}
                    </Box>
                    {selectedFilterOption === PlantStructureFilterOptions.Removed &&
                    canRestoreDepartments ? (
                      <PlantStructureTree
                        selectedFilterOption={selectedFilterOption}
                        hook={useGetRemovedPlantStructure}
                        nodeComponent={RestorePlantStructureNode}
                      />
                    ) : (
                      <PlantStructureTree
                        selectedFilterOption={selectedFilterOption}
                        nodeComponent={PlantStructureNode}
                      />
                    )}
                  </Stack>
                </CardBox>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Outlet />
              </Grid>
            </Grid>
          </ItemDetailsProvider>
        </PageLayout>
      </ItemLinkProvider>
    </SelectedItemProvider>
  )
}
